import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import axios from 'axios';
import './Analytics.css';
const LiveFeed = React.lazy(() => import('./LiveFeed'));

const Analytics = () => {
    const history = useHistory();
    const [dashboard, setDashboard] = useState(null);
    const [trends, setTrends] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [trendsDays, setTrendsDays] = useState(30);
    const [trendsEndDate, setTrendsEndDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [trendsAllTime, setTrendsAllTime] = useState(false);
    const [comparisonPeriod, setComparisonPeriod] = useState('daily');
    const [yearlyComparisonData, setYearlyComparisonData] = useState([]);
    const [splitPromoTests, setSplitPromoTests] = useState(false);
    const [activeIndicators, setActiveIndicators] = useState({
        frontendLogs: true,
        pageViews: true,
        errors: true,
        pinRequests: true,
        testCompleted: true,
        testCompletedPromo: true,
        testCompletedPaid: true,
        emailsSent: true,
        emailsOpened: true
    });

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    const todayDateKey = useMemo(() => new Date().toISOString().split('T')[0], []);

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const code = sessionStorage.getItem('code');
            if (code !== '1000') {
                setError('Analytics access is restricted to superadmin users only.');
                setLoading(false);
                return;
            }

            let token = sessionStorage.getItem('token');
            
            if (!token) {
                setError('Authentication required. Please log in.');
                setLoading(false);
                return;
            }

            // Remove quotes if token is JSON-stringified
            try {
                token = JSON.parse(token);
            } catch {
                // Token is already a plain string
            }

            console.log('Fetching analytics dashboard with token:', token ? 'present' : 'missing');
            
            const response = await axios.get(
                `${backendUrl}/analytics/dashboard`,
                {
                    headers: { 'x-access-token': token }
                }
            );

            console.log('Analytics response:', response.data);

            if (response.data.success) {
                setDashboard(response.data.dashboard);
            } else {
                setError(response.data.message || 'Failed to load analytics data');
            }
        } catch (err) {
            console.error('Error fetching analytics:', err);
            if (err.response?.status === 401 || err.response?.data?.auth === false) {
                setError('Authentication failed. Please log in again.');
            } else {
                setError(err.response?.data?.error || err.message || 'Failed to load analytics');
            }
        } finally {
            setLoading(false);
        }
    }, [backendUrl]);

    const fetchTrends = useCallback(async () => {
        try {
            const code = sessionStorage.getItem('code');
            if (code !== '1000') {
                return;
            }

            let token = sessionStorage.getItem('token');
            if (!token) return;

            try {
                token = JSON.parse(token);
            } catch {
                // Token is already a plain string
            }

            const response = await axios.get(
                `${backendUrl}/analytics/trends?days=${trendsDays}&endDate=${encodeURIComponent(trendsEndDate)}&allTime=${trendsAllTime ? '1' : '0'}`,
                {
                    headers: { 'x-access-token': token }
                }
            );

            if (response.data.success) {
                setTrends(response.data.trends);
            }
        } catch (err) {
            console.error('Error fetching trends:', err);
        }
    }, [backendUrl, trendsDays, trendsEndDate, trendsAllTime]);

    const fetchYearlyComparisonData = useCallback(async () => {
        try {
            const code = sessionStorage.getItem('code');
            if (code !== '1000') {
                return;
            }

            let token = sessionStorage.getItem('token');
            if (!token) return;

            try {
                token = JSON.parse(token);
            } catch {
                // Token is already a plain string
            }

            const response = await axios.get(
                `${backendUrl}/analytics/trends?groupBy=yearly`,
                {
                    headers: { 'x-access-token': token }
                }
            );

            if (response.data.success) {
                setYearlyComparisonData(response.data.trends || []);
            }
        } catch (err) {
            console.error('Error fetching yearly comparison trends:', err);
        }
    }, [backendUrl]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (activeTab === 'trends') {
            fetchTrends();
        }
    }, [activeTab, fetchTrends]);

    useEffect(() => {
        if (activeTab === 'trends' && comparisonPeriod === 'yearly') {
            fetchYearlyComparisonData();
        }
    }, [activeTab, comparisonPeriod, fetchYearlyComparisonData]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString();
    };

    const toggleIndicator = (indicator) => {
        setActiveIndicators(prev => ({
            ...prev,
            [indicator]: !prev[indicator]
        }));
    };

    const setComparisonMode = (period) => {
        setComparisonPeriod(period);

        if (period === 'weekly') {
            setTrendsDays(180);
            setTrendsEndDate(todayDateKey);
            setTrendsAllTime(false);
        }

        if (period === 'quarterly') {
            setTrendsDays(365);
            setTrendsEndDate(todayDateKey);
            setTrendsAllTime(false);
        }
    };

    const setComparisonRangeDays = (days) => {
        setTrendsDays(days);
        setTrendsEndDate(todayDateKey);
        setTrendsAllTime(false);
    };

    const setComparisonAllTime = () => {
        setTrendsAllTime(true);
        setTrendsEndDate(todayDateKey);
    };

    const shiftTrendWindow = (direction) => {
        setTrendsEndDate(prev => {
            const currentEndDate = new Date(prev);
            if (Number.isNaN(currentEndDate.getTime())) {
                return prev;
            }

            currentEndDate.setUTCDate(currentEndDate.getUTCDate() + (direction * trendsDays));

            const nextKey = currentEndDate.toISOString().split('T')[0];
            if (direction > 0 && nextKey > todayDateKey) {
                return todayDateKey;
            }

            return nextKey;
        });
    };

    const trendRangeLabel = useMemo(() => {
        if (!trends || trends.length === 0) {
            return '';
        }

        const firstDate = trends[0]?.date;
        const lastDate = trends[trends.length - 1]?.date;
        if (!firstDate || !lastDate) {
            return '';
        }

        return `${new Date(firstDate).toLocaleDateString()} - ${new Date(lastDate).toLocaleDateString()}`;
    }, [trends]);

    const canGoForward = !trendsAllTime && trendsEndDate < todayDateKey;

    const comparisonData = useMemo(() => {
        if (comparisonPeriod === 'yearly') {
            return yearlyComparisonData || [];
        }

        if (!trends || trends.length === 0) {
            return [];
        }

        const getDateKey = (dateValue) => {
            const date = new Date(dateValue);
            if (Number.isNaN(date.getTime())) return null;

            if (comparisonPeriod === 'daily') {
                return {
                    key: date.toISOString().split('T')[0],
                    sortDate: date,
                    label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                };
            }

            if (comparisonPeriod === 'weekly') {
                const weekStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
                const day = weekStart.getUTCDay();
                const diff = day === 0 ? -6 : 1 - day;
                weekStart.setUTCDate(weekStart.getUTCDate() + diff);
                const key = weekStart.toISOString().split('T')[0];
                return {
                    key,
                    sortDate: weekStart,
                    label: `Week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                };
            }

            if (comparisonPeriod === 'monthly') {
                const monthStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
                const key = `${monthStart.getUTCFullYear()}-${String(monthStart.getUTCMonth() + 1).padStart(2, '0')}`;
                return {
                    key,
                    sortDate: monthStart,
                    label: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                };
            }

            if (comparisonPeriod === 'quarterly') {
                const quarterStartMonth = Math.floor(date.getUTCMonth() / 3) * 3;
                const quarterStart = new Date(Date.UTC(date.getUTCFullYear(), quarterStartMonth, 1));
                const quarterNumber = Math.floor(quarterStartMonth / 3) + 1;
                const key = `${quarterStart.getUTCFullYear()}-Q${quarterNumber}`;

                return {
                    key,
                    sortDate: quarterStart,
                    label: `Q${quarterNumber} ${quarterStart.getUTCFullYear()}`
                };
            }

            const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
            return {
                key: String(yearStart.getUTCFullYear()),
                sortDate: yearStart,
                label: String(yearStart.getUTCFullYear())
            };
        };

        const grouped = trends.reduce((acc, point) => {
            const keyData = getDateKey(point.date);
            if (!keyData) {
                return acc;
            }

            const { key, sortDate, label } = keyData;
            if (!acc[key]) {
                acc[key] = {
                    label,
                    sortDate,
                    frontendLogs: 0,
                    pageViews: 0,
                    errors: 0,
                    pinRequests: 0,
                    testCompleted: 0,
                    testCompletedPromo: 0,
                    testCompletedPaid: 0
                };
            }

            acc[key].frontendLogs += point.frontendLogs || 0;
            acc[key].pageViews += point.pageViews || 0;
            acc[key].errors += point.errors || 0;
            acc[key].pinRequests += point.pinRequests || 0;
            acc[key].testCompleted += point.testCompleted || 0;
            acc[key].testCompletedPromo += point.testCompletedPromo || 0;
            acc[key].testCompletedPaid += point.testCompletedPaid || 0;

            return acc;
        }, {});

        return Object.values(grouped)
            .sort((a, b) => a.sortDate - b.sortDate)
            .map(({ sortDate, ...rest }) => rest);
    }, [trends, comparisonPeriod, yearlyComparisonData]);

    const chartComparisonData = useMemo(() => {
        return (comparisonData || []).map((point) => {
            const promo = Number(point.testCompletedPromo || 0);
            const paid = Number(point.testCompletedPaid || 0);

            let promoVsPaidLabel = '';
            if (splitPromoTests && promo > 0 && paid > 0) {
                promoVsPaidLabel = `${((promo / paid) * 100).toFixed(0)}%`;
            }

            return {
                ...point,
                promoVsPaidLabel
            };
        });
    }, [comparisonData, splitPromoTests]);

    if (loading) {
        return (
            <div className="analytics-container">
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="analytics-container">
                <div className="analytics-error">
                    <h2>Error Loading Analytics</h2>
                    <p>{error}</p>
                    <button onClick={fetchDashboard}>Retry</button>
                </div>
            </div>
        );
    }

    if (!dashboard) {
        return (
            <div className="analytics-container">
                <p>No analytics data available</p>
            </div>
        );
    }

    const { summary, recentErrors, recentPinRequests, pageViews, totalPageViews } = dashboard;

    return (
        <div className="analytics-container">
            <div className="analytics-header">
                <button onClick={() => history.push('/manager')} className="back-btn">
                    ← Back to Admin
                </button>
                <h1>📊 Analytics Dashboard</h1>
                <button onClick={fetchDashboard} className="refresh-btn">
                    🔄 Refresh
                </button>
            </div>

            <div className="analytics-tabs">
                <button
                    className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button
                    className={`tab ${activeTab === 'pageviews' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pageviews')}
                >
                    Page Views
                </button>
                <button
                    className={`tab ${activeTab === 'errors' ? 'active' : ''}`}
                    onClick={() => setActiveTab('errors')}
                >
                    Errors
                </button>
                <button
                    className={`tab ${activeTab === 'emails' ? 'active' : ''}`}
                    onClick={() => setActiveTab('emails')}
                >
                    Email Analytics
                </button>
                <button
                    className={`tab ${activeTab === 'trends' ? 'active' : ''}`}
                    onClick={() => setActiveTab('trends')}
                >
                    Trends
                </button>
                <button
                    className={`tab ${activeTab === 'live' ? 'active' : ''}`}
                    onClick={() => setActiveTab('live')}
                >
                    Live Feed
                </button>
            </div>

            {activeTab === 'overview' && (
                <div className="analytics-content">
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <h3>Frontend Logs</h3>
                            <div className="metric-value">{summary.totalLogs.toLocaleString()}</div>
                            <div className="metric-label">Total Events</div>
                            <div className="metric-subtext">
                                Last 7 days: {summary.logs7Days.toLocaleString()} | 
                                Last 30 days: {summary.logs30Days.toLocaleString()}
                            </div>
                        </div>

                        <div className="metric-card">
                            <h3>Page Views</h3>
                            <div className="metric-value">{totalPageViews.toLocaleString()}</div>
                            <div className="metric-label">Last 7 Days</div>
                        </div>

                        <div className="metric-card">
                            <h3>Emails Sent</h3>
                            <div className="metric-value">{summary.emailsTotal.toLocaleString()}</div>
                            <div className="metric-label">Total Emails</div>
                        </div>

                        <div className="metric-card">
                            <h3>Email Open Rate</h3>
                            <div className="metric-value">{summary.emailOpenRate}%</div>
                            <div className="metric-label">
                                {summary.emailsOpened} / {summary.emailsTotal} opened
                            </div>
                        </div>

                        <div className="metric-card">
                            <h3>Email Click Rate</h3>
                            <div className="metric-value">{summary.emailClickRate}%</div>
                            <div className="metric-label">
                                {summary.emailsClicked} / {summary.emailsTotal} clicked
                            </div>
                        </div>

                        <div className="metric-card error-card">
                            <h3>Recent Errors</h3>
                            <div className="metric-value">{recentErrors.length}</div>
                            <div className="metric-label">Last 10 Errors</div>
                        </div>

                        <div className="metric-card">
                            <h3>PIN Requests</h3>
                            <div className="metric-value">{summary.totalPinRequests.toLocaleString()}</div>
                            <div className="metric-label">Total Requests</div>
                            <div className="metric-subtext">
                                Last 7 days: {summary.pinRequests7Days.toLocaleString()} | 
                                Last 30 days: {summary.pinRequests30Days.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    {recentErrors.length > 0 && (
                        <div className="section">
                            <h2>Recent Errors (Last 10)</h2>
                            <div className="error-list">
                                {recentErrors.map((error, index) => (
                                    <div key={index} className="error-item">
                                        <div className="error-header">
                                            <span className="error-type">{error.type}</span>
                                            <span className="error-time">{formatDate(error.timestamp)}</span>
                                        </div>
                                        <div className="error-message">{error.message}</div>
                                        <div className="error-details">
                                            <span>User: {error.userEmail || 'anonymous'}</span>
                                            {error.url && <span>URL: {error.url}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {recentPinRequests && recentPinRequests.length > 0 && (
                        <div className="section">
                            <h2>Recent PIN Requests (Last 10)</h2>
                            <div className="pin-request-list">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Timestamp</th>
                                            <th>Email</th>
                                            <th>Key</th>
                                            <th>Test ID</th>
                                            <th>IP</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recentPinRequests.map((request, index) => (
                                            <tr key={index}>
                                                <td>{formatDate(request.timestamp)}</td>
                                                <td>{request.userEmail}</td>
                                                <td>{request.userKey}</td>
                                                <td>{request.testID || '-'}</td>
                                                <td>{request.ip}</td>
                                                <td>
                                                    <span className={`status-badge ${request.pinSent ? 'success' : 'pending'}`}>
                                                        {request.pinSent ? 'Sent' : 'Created'}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'pageviews' && (
                <div className="analytics-content">
                    <div className="section">
                        <h2>Page Views (Last 7 Days)</h2>
                        <div className="chart-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Page</th>
                                        <th>Views</th>
                                        <th>Percentage</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(pageViews)
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([page, views]) => (
                                            <tr key={page}>
                                                <td>{page}</td>
                                                <td>{views}</td>
                                                <td>
                                                    <div className="percentage-bar">
                                                        <div 
                                                            className="percentage-fill" 
                                                            style={{ width: `${(views / totalPageViews) * 100}%` }}
                                                        />
                                                        <span>{((views / totalPageViews) * 100).toFixed(1)}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'errors' && (
                <div className="analytics-content">
                    <div className="section">
                        <h2>All Recent Errors</h2>
                        {recentErrors.length === 0 ? (
                            <p className="no-data">No errors recorded 🎉</p>
                        ) : (
                            <div className="error-list">
                                {recentErrors.map((error, index) => (
                                    <div key={index} className="error-item">
                                        <div className="error-header">
                                            <span className="error-type">{error.type}</span>
                                            <span className="error-time">{formatDate(error.timestamp)}</span>
                                        </div>
                                        <div className="error-message">{error.message}</div>
                                        <div className="error-details">
                                            <span>User: {error.userEmail || 'anonymous'}</span>
                                            {error.url && <span>URL: {error.url}</span>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'emails' && (
                <div className="analytics-content">
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <h3>Total Sent</h3>
                            <div className="metric-value">{summary.emailsTotal}</div>
                        </div>
                        <div className="metric-card">
                            <h3>Opened</h3>
                            <div className="metric-value">{summary.emailsOpened}</div>
                            <div className="metric-label">{summary.emailOpenRate}% open rate</div>
                        </div>
                        <div className="metric-card">
                            <h3>Clicked</h3>
                            <div className="metric-value">{summary.emailsClicked}</div>
                            <div className="metric-label">{summary.emailClickRate}% click rate</div>
                        </div>
                    </div>
                    <div className="section">
                        <p className="info-text">
                            💡 <strong>View per-user analytics:</strong> In the Admin panel's user list, click the 📊 button next to any user to see their detailed email analytics (opens, clicks, timestamps).
                        </p>
                    </div>
                </div>
            )}

            {activeTab === 'trends' && (
                <div className="analytics-content">
                    <div className="section">
                        <div className="section-header">
                            <h2>Activity Trends</h2>
                            <div className="day-selector">
                                <button 
                                    className={trendsDays === 7 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(7);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    7 Days
                                </button>
                                <button 
                                    className={trendsDays === 14 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(14);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    14 Days
                                </button>
                                <button 
                                    className={trendsDays === 30 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(30);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    30 Days
                                </button>
                                <button 
                                    className={trendsDays === 60 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(60);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    60 Days
                                </button>
                                <button 
                                    className={trendsDays === 90 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(90);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    90 Days
                                </button>
                                <button 
                                    className={trendsDays === 180 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(180);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    180 Days
                                </button>
                                <button 
                                    className={trendsDays === 365 ? 'active' : ''} 
                                    onClick={() => {
                                        setTrendsDays(365);
                                        setTrendsAllTime(false);
                                    }}
                                >
                                    12 Months
                                </button>
                                <button
                                    className={trendsAllTime ? 'active' : ''}
                                    onClick={() => setTrendsAllTime(true)}
                                >
                                    All Time
                                </button>
                            </div>
                        </div>

                        <div className="day-selector" style={{ marginBottom: '16px' }}>
                            <button onClick={() => shiftTrendWindow(-1)} disabled={trendsAllTime}>← Previous</button>
                            <button onClick={() => shiftTrendWindow(1)} disabled={!canGoForward}>Next →</button>
                            <span style={{ alignSelf: 'center', color: '#666', fontSize: '14px' }}>
                                {trendsAllTime ? 'All available history' : (trendRangeLabel || 'Loading range...')}
                            </span>
                        </div>

                        {trends && trends.length > 0 ? (
                            <>
                                <div className="chart-container">
                                    <h3>Activity Over Time</h3>
                                    <div className="chart-filters">
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.frontendLogs}
                                                onChange={() => toggleIndicator('frontendLogs')}
                                            />
                                            <span style={{color: '#8884d8'}}>⬤</span> Frontend Logs
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.pageViews}
                                                onChange={() => toggleIndicator('pageViews')}
                                            />
                                            <span style={{color: '#82ca9d'}}>⬤</span> Page Views
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.errors}
                                                onChange={() => toggleIndicator('errors')}
                                            />
                                            <span style={{color: '#ff7c7c'}}>⬤</span> Errors
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.pinRequests}
                                                onChange={() => toggleIndicator('pinRequests')}
                                            />
                                            <span style={{color: '#ffc658'}}>⬤</span> PIN Requests
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={splitPromoTests}
                                                onChange={() => setSplitPromoTests(prev => !prev)}
                                            />
                                            Split Promo/Paid Tests
                                        </label>
                                        {!splitPromoTests && (
                                            <label>
                                                <input 
                                                    type="checkbox" 
                                                    checked={activeIndicators.testCompleted}
                                                    onChange={() => toggleIndicator('testCompleted')}
                                                />
                                                <span style={{color: '#8dd1e1'}}>⬤</span> Test Completed
                                            </label>
                                        )}
                                        {splitPromoTests && (
                                            <>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={activeIndicators.testCompletedPromo}
                                                        onChange={() => toggleIndicator('testCompletedPromo')}
                                                    />
                                                    <span style={{color: '#00acc1'}}>⬤</span> Test Completed (Promo)
                                                </label>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={activeIndicators.testCompletedPaid}
                                                        onChange={() => toggleIndicator('testCompletedPaid')}
                                                    />
                                                    <span style={{color: '#1e88e5'}}>⬤</span> Test Completed (Paid)
                                                </label>
                                            </>
                                        )}
                                    </div>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <LineChart data={trends}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis 
                                                dataKey="date" 
                                                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            />
                                            <YAxis />
                                            <Tooltip 
                                                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                            />
                                            <Legend onClick={(e) => e.dataKey && toggleIndicator(e.dataKey)} />
                                            {activeIndicators.frontendLogs && <Line type="monotone" dataKey="frontendLogs" stroke="#8884d8" name="Frontend Logs" />}
                                            {activeIndicators.pageViews && <Line type="monotone" dataKey="pageViews" stroke="#82ca9d" name="Page Views" />}
                                            {activeIndicators.errors && <Line type="monotone" dataKey="errors" stroke="#ff7c7c" name="Errors" />}
                                            {activeIndicators.pinRequests && <Line type="monotone" dataKey="pinRequests" stroke="#ffc658" name="PIN Requests" />}
                                            {!splitPromoTests && activeIndicators.testCompleted && <Line type="monotone" dataKey="testCompleted" stroke="#8dd1e1" name="Test Completed" />}
                                            {splitPromoTests && activeIndicators.testCompletedPromo && <Line type="monotone" dataKey="testCompletedPromo" stroke="#00acc1" name="Test Completed (Promo)" />}
                                            {splitPromoTests && activeIndicators.testCompletedPaid && <Line type="monotone" dataKey="testCompletedPaid" stroke="#1e88e5" name="Test Completed (Paid)" />}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="chart-container">
                                    <h3>Email Activity</h3>
                                    <div className="chart-filters">
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.emailsSent}
                                                onChange={() => toggleIndicator('emailsSent')}
                                            />
                                            <span style={{color: '#8884d8'}}>⬤</span> Emails Sent
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.emailsOpened}
                                                onChange={() => toggleIndicator('emailsOpened')}
                                            />
                                            <span style={{color: '#82ca9d'}}>⬤</span> Emails Opened
                                        </label>
                                    </div>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <LineChart data={trends}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis 
                                                dataKey="date" 
                                                tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                            />
                                            <YAxis />
                                            <Tooltip 
                                                labelFormatter={(date) => new Date(date).toLocaleDateString()}
                                            />
                                            <Legend onClick={(e) => e.dataKey && toggleIndicator(e.dataKey)} />
                                            {activeIndicators.emailsSent && <Line type="monotone" dataKey="emailsSent" stroke="#8884d8" name="Emails Sent" />}
                                            {activeIndicators.emailsOpened && <Line type="monotone" dataKey="emailsOpened" stroke="#82ca9d" name="Emails Opened" />}
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>

                                <div className="chart-container">
                                    <h3>Daily Activity Comparison</h3>
                                    <div className="day-selector" style={{ marginBottom: '16px' }}>
                                        <button
                                            className={comparisonPeriod === 'daily' ? 'active' : ''}
                                            onClick={() => setComparisonMode('daily')}
                                        >
                                            Daily
                                        </button>
                                        <button
                                            className={comparisonPeriod === 'weekly' ? 'active' : ''}
                                            onClick={() => setComparisonMode('weekly')}
                                        >
                                            Weekly
                                        </button>
                                        <button
                                            className={comparisonPeriod === 'monthly' ? 'active' : ''}
                                            onClick={() => setComparisonMode('monthly')}
                                        >
                                            Monthly
                                        </button>
                                        <button
                                            className={comparisonPeriod === 'quarterly' ? 'active' : ''}
                                            onClick={() => setComparisonMode('quarterly')}
                                        >
                                            Quarterly
                                        </button>
                                        <button
                                            className={comparisonPeriod === 'yearly' ? 'active' : ''}
                                            onClick={() => setComparisonMode('yearly')}
                                        >
                                            Yearly
                                        </button>
                                    </div>
                                    {comparisonPeriod !== 'yearly' && (
                                        <div className="day-selector" style={{ marginBottom: '12px' }}>
                                            <button
                                                className={trendsDays === 90 ? 'active' : ''}
                                                onClick={() => setComparisonRangeDays(90)}
                                            >
                                                3 Months
                                            </button>
                                            <button
                                                className={trendsDays === 180 ? 'active' : ''}
                                                onClick={() => setComparisonRangeDays(180)}
                                            >
                                                6 Months
                                            </button>
                                            <button
                                                className={trendsDays === 365 ? 'active' : ''}
                                                onClick={() => setComparisonRangeDays(365)}
                                            >
                                                12 Months
                                            </button>
                                            <button
                                                className={trendsAllTime ? 'active' : ''}
                                                onClick={() => setComparisonAllTime()}
                                            >
                                                All Time
                                            </button>
                                        </div>
                                    )}
                                    {comparisonPeriod === 'yearly' && (
                                        <div style={{ marginBottom: '12px', fontSize: '13px', color: '#666' }}>
                                            Showing all-time full calendar-year totals.
                                        </div>
                                    )}
                                    <div className="chart-filters">
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.frontendLogs}
                                                onChange={() => toggleIndicator('frontendLogs')}
                                            />
                                            <span style={{color: '#8884d8'}}>⬤</span> Frontend Logs
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.pageViews}
                                                onChange={() => toggleIndicator('pageViews')}
                                            />
                                            <span style={{color: '#82ca9d'}}>⬤</span> Page Views
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.errors}
                                                onChange={() => toggleIndicator('errors')}
                                            />
                                            <span style={{color: '#ff7c7c'}}>⬤</span> Errors
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={activeIndicators.pinRequests}
                                                onChange={() => toggleIndicator('pinRequests')}
                                            />
                                            <span style={{color: '#ffc658'}}>⬤</span> PIN Requests
                                        </label>
                                        <label>
                                            <input 
                                                type="checkbox" 
                                                checked={splitPromoTests}
                                                onChange={() => setSplitPromoTests(prev => !prev)}
                                            />
                                            Split Promo/Paid Tests
                                        </label>
                                        {!splitPromoTests && (
                                            <label>
                                                <input 
                                                    type="checkbox" 
                                                    checked={activeIndicators.testCompleted}
                                                    onChange={() => toggleIndicator('testCompleted')}
                                                />
                                                <span style={{color: '#8dd1e1'}}>⬤</span> Test Completed
                                            </label>
                                        )}
                                        {splitPromoTests && (
                                            <>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={activeIndicators.testCompletedPromo}
                                                        onChange={() => toggleIndicator('testCompletedPromo')}
                                                    />
                                                    <span style={{color: '#00acc1'}}>⬤</span> Test Completed (Promo)
                                                </label>
                                                <label>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={activeIndicators.testCompletedPaid}
                                                        onChange={() => toggleIndicator('testCompletedPaid')}
                                                    />
                                                    <span style={{color: '#1e88e5'}}>⬤</span> Test Completed (Paid)
                                                </label>
                                            </>
                                        )}
                                    </div>
                                    <ResponsiveContainer width="100%" height={400}>
                                        <BarChart data={chartComparisonData} margin={{ top: 28, right: 20, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="label" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend onClick={(e) => e.dataKey && toggleIndicator(e.dataKey)} />
                                            {activeIndicators.frontendLogs && <Bar dataKey="frontendLogs" fill="#8884d8" name="Frontend Logs" />}
                                            {activeIndicators.pageViews && <Bar dataKey="pageViews" fill="#82ca9d" name="Page Views" />}
                                            {activeIndicators.errors && <Bar dataKey="errors" fill="#ff7c7c" name="Errors" />}
                                            {activeIndicators.pinRequests && <Bar dataKey="pinRequests" fill="#ffc658" name="PIN Requests" />}
                                            {!splitPromoTests && activeIndicators.testCompleted && <Bar dataKey="testCompleted" fill="#8dd1e1" name="Test Completed" />}
                                            {splitPromoTests && activeIndicators.testCompletedPromo && (
                                                <Bar dataKey="testCompletedPromo" fill="#00acc1" name="Test Completed (Promo)">
                                                    <LabelList dataKey="promoVsPaidLabel" position="top" fill="#00838f" fontSize={11} />
                                                </Bar>
                                            )}
                                            {splitPromoTests && activeIndicators.testCompletedPaid && <Bar dataKey="testCompletedPaid" fill="#1e88e5" name="Test Completed (Paid)" />}
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </>
                        ) : (
                            <p className="info-text">Loading trends data...</p>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'live' && (
                <div className="analytics-content">
                    <div className="section">
                        <h2>Live Logs Feed</h2>
                        <p className="info-text">Streaming latest backend logs; frontend events are labeled <strong>FRONT</strong>.</p>
                        <div style={{marginTop:12}}>
                            {/* Lazy load the LiveFeed component */}
                            <React.Suspense fallback={<div>Connecting to live logs...</div>}>
                                <LiveFeed />
                            </React.Suspense>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Analytics;
